import { useId, useRef, useState, type ReactNode } from 'react';
import Popover from '../../../components/base/Popover';

/**
 * One pill in the /search filter bar: a rounded button that names its filter
 * (or shows the value chosen) and opens that filter's controls in a popover.
 *
 * Wasalt-style (owner, 2026-10-07). A pill holding a non-default value takes the
 * THEME.md decision-D3 selection recipe (teal border, sunken fill, teal text), so
 * the bar itself shows what is filtered without a separate row of chips.
 */
export default function FilterPill({
  label,
  active = false,
  icon,
  panelWidth = '22rem',
  panelLabel,
  children,
}: {
  /** The filter's name, or the value chosen («2+ غرف», «فيلا»…). */
  label: ReactNode;
  /** True when the filter holds a non-default value. */
  active?: boolean;
  icon?: ReactNode;
  /** Popover width (CSS length). */
  panelWidth?: string;
  /** Accessible name of the popover (the filter's plain name). */
  panelLabel: string;
  /** The controls. Receives `close` for a «تم» / pick-to-close control. */
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        className={[
          'inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-4 text-[0.875rem] font-medium transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30',
          active || open
            ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]'
            : 'border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] text-[var(--pv-color-text)] hover:border-[var(--pv-color-primary)]',
        ].join(' ')}
      >
        {icon}
        <span className="max-w-[12rem] truncate">{label}</span>
        <i
          className={`ri-arrow-down-s-line text-base leading-none transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      <Popover
        open={open}
        onOpenChange={setOpen}
        anchorRef={anchorRef}
        width={panelWidth}
        id={panelId}
        role="dialog"
        aria-label={panelLabel}
        className="p-4"
      >
        {children(close)}
      </Popover>
    </>
  );
}
